<template>
  <div class="crom" :class="[`crom--${variant}`]">
    <!-- 0. Loading / Skeleton State -->
    <div v-if="loading">
      <CreditRiskMeterSkeleton 
        :variant="variant" 
        :show-disclaimer="showDisclaimer" 
        :compact-width="compactWidth" 
      />
    </div>

    <!-- 1. Fallback State: UNRATED -->
    <div v-else-if="state === 'UNRATED' && variant === 'full'" class="crom-banner crom-banner--unrated" role="alert">
      <span class="crom-banner__icon" aria-hidden="true">⚠️</span>
      <div class="crom-banner__text">
        Credit rating not available for this security. It cannot be invested in until a rating from a SEBI-registered CRA is recorded.
      </div>
    </div>

    <!-- 2. Fallback State: SOVEREIGN -->
    <div v-else-if="state === 'SOVEREIGN' && variant === 'full'" class="crom-banner crom-banner--sovereign" role="status">
      <span class="crom-banner__icon" aria-hidden="true">🏛️</span>
      <div class="crom-banner__text">
        Government security. Not rated by a credit rating agency, so the Credit Risk-o-Meter does not apply.
      </div>
    </div>

    <!-- 3. Standard State: OK -->
    <div v-else-if="state === 'OK'">
      <!-- Variant A: Full Meter (Refactored to match Figma split-column details page) -->
      <div v-if="variant === 'full'" class="crom-full-layout">
        <div class="crom-full-body">
          <!-- Left Column: Structured Details -->
          <div class="crom-full-details-col">
            <!-- 1. Rating Summary Box -->
            <div class="crom-rating-summary-box">
              <div class="crom-rating-summary-box__rating">
                {{ isInc ? 'INC' : governingSymbol }}
              </div>
              <div class="crom-rating-summary-box__text">
                <span class="risk-label">{{ isInc ? 'ISSUER NOT COOPERATING' : LABELS[level - 1]?.toUpperCase() }}</span>
                <span class="rating-agency-val">{{ governingCra }} {{ governingSymbol }}</span>
              </div>
            </div>

            <!-- 2. Structured List rows (no borders, spaced cleanly) -->
            <div class="crom-details-rows-col">
              <div class="crom-details-row-clean">
                <span class="label">Rating agency</span>
                <strong class="value">{{ governingCra }}</strong>
              </div>
              <div v-if="props.securityLabel || securityStatus !== 'UNKNOWN'" class="crom-details-row-clean">
                <strong class="value crom-details-security-val">
                  <span v-if="props.securityLabel" :class="{ 'uns': securityStatus === 'UNSECURED' || props.securityLabel?.toLowerCase().includes('unsecured') }">{{ props.securityLabel }}</span>
                  <span v-else-if="securityStatus === 'UNSECURED'" class="uns">Unsecured</span>
                  <span v-else-if="securityStatus === 'SECURED'">Senior secured</span>
                </strong>
              </div>
              <div v-if="formattedRatingDate" class="crom-details-row-clean">
                <span class="label crom-details-date-label">Rating as of {{ formattedRatingDate }}</span>
              </div>
              <div v-if="allRatings.length > 1" class="crom-details-row-clean crom-all-ratings-row">
                <span class="label crom-all-ratings-title">All Ratings</span>
                <div class="crom-all-ratings-list">
                  <div 
                    v-for="r in allRatings" 
                    :key="r.id || r.craCode || r.cra_code"
                    class="crom-all-ratings-item"
                  >
                    <span class="crom-all-ratings-item__cra">
                      {{ r.craCode || r.cra_code }}
                      <span v-if="r.ratingDate || r.rating_date" class="crom-all-ratings-item__date">
                        ({{ formatSingleRatingDate(r.ratingDate || r.rating_date) }})
                      </span>
                    </span>
                    <span class="crom-all-ratings-item__sym">{{ r.symbol || r.ratingRaw || r.rating_raw }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Right Column: Visual Semicircle Gauge -->
          <div class="crom-full-gauge-col">
            <div class="crom-svg-container">
              <svg viewBox="0 0 600 346" width="100%" role="img" :aria-label="ariaText">
                <!-- Background semi-circle shadow/arc -->
                <path d="M59,266 A221,221 0 0 1 501,266 Z" fill="#eee9f7" opacity="0.7" />
                
                <!-- Semicircle Segments -->
                <g v-for="i in nSegments" :key="i - 1">
                  <path 
                    :d="getSegmentPath(280, 262, 92, 165, 180 - (i - 1) * segmentStep, 180 - i * segmentStep)" 
                    :fill="getSegmentColor(i - 1)" 
                    stroke="#fff" 
                    stroke-width="1.5" 
                  />
                  
                  <!-- Segment Text/Symbols -->
                  <g v-for="(symbolText, k) in getSegmentSymbols(i - 1)" :key="symbolText">
                    <text 
                      x="280" 
                      :y="262 - getSymbolRadius(i - 1, k)" 
                      :transform="`rotate(${90 - getSegmentMiddleAngle(i - 1)} 280 262)`" 
                      text-anchor="middle" 
                      dy="0.35em" 
                      font-size="13" 
                      font-weight="700" 
                      :fill="getSymbolColor(i - 1)"
                    >
                      {{ symbolText }}
                    </text>
                  </g>
                  
                  <!-- Outer Labels (Lowest Risk, etc.) -->
                  <g v-for="(lineText, k) in getSegmentOuterLabels(i - 1)" :key="k">
                    <text 
                      :x="getOuterLabelPosition(i - 1).x" 
                      :y="getOuterLabelPosition(i - 1).y + (k - (getSegmentOuterLabels(i - 1).length - 1) / 2) * 14" 
                      text-anchor="middle" 
                      dy="0.35em" 
                      font-size="13" 
                      fill="#19204D"
                    >
                      {{ lineText }}
                    </text>
                  </g>
                </g>
                
                <!-- Needle (Figma-exact tapered single polygon) -->
                <g :style="{ transform: `rotate(${needleRotation}deg)`, transformOrigin: '280px 262px', transition: 'transform 1.6s cubic-bezier(0.25, 0.8, 0.25, 1)' }">
                  <polygon 
                    :points="getNeedleEndpoints" 
                    fill="#19204D" 
                  />
                  <circle 
                    :cx="280" 
                    :cy="262" 
                    r="11" 
                    fill="#19204D" 
                    stroke="#fff" 
                    stroke-width="2" 
                  />
                </g>
                
                <!-- Bottom separating line -->
                <line x1="14" y1="284" x2="546" y2="284" stroke="#c9cfe0" />
                
                <!-- Labels bottom -->
                <text x="20" y="302" font-size="13" fill="#5b6784">Risk profile</text>
                <text x="220" y="302" font-size="13" fill="#5b6784">Risk scale</text>
              </svg>
            </div>
          </div>
        </div>

        <!-- Stale Rating Warning Banner -->
        <div v-if="isRatingStale" class="crom-banner crom-banner--stale" role="alert" aria-live="polite">
          <span class="crom-banner__icon" aria-hidden="true">⚠️</span>
          <div class="crom-banner__text">
            This credit rating is older than 12 months and is currently under surveillance by the CRA.
          </div>
        </div>
        
        <!-- Disclaimer -->
        <div v-if="showDisclaimer" class="crom-disclaimer">
          {{ disclaimerText }}
        </div>
      </div>

      <!-- Variant B: Compact Meter -->
      <div v-else-if="variant === 'compact'" class="crom-compact-layout">
        <div class="crom-compact-svg">
          <svg viewBox="0 0 140 82" :width="compactWidth" role="img" :aria-label="ariaText">
            <!-- Semicircle Segments -->
            <path 
              v-for="i in nSegments" 
              :key="i - 1" 
              :d="getSegmentPath(70, 70, 38, 62, 180 - (i - 1) * segmentStep - 0.8, 180 - i * segmentStep + 0.8)" 
              :fill="getSegmentColor(i - 1)" 
            />
            
            <!-- Needle (Figma-exact tapered single polygon) -->
            <g :style="{ transform: `rotate(${needleRotation}deg)`, transformOrigin: '70px 70px', transition: 'transform 1.6s cubic-bezier(0.25, 0.8, 0.25, 1)' }">
              <polygon 
                :points="getCompactNeedle" 
                fill="#19204D" 
              />
              <circle 
                :cx="70" 
                :cy="70" 
                r="6" 
                fill="#19204D" 
                stroke="#fff" 
                stroke-width="2" 
              />
            </g>
          </svg>
        </div>

        <div class="crom-compact-info">
          <div class="crom-compact-info__rating">
            <strong>{{ isInc ? 'INC' : governingSymbol }}</strong>
            <button 
              type="button" 
              class="crom-info-btn" 
              aria-label="About Credit Risk-o-Meter"
              @click.stop="showFullDialog = true"
            >
              i
            </button>
          </div>
          <div class="crom-compact-info__cra">{{ governingCra }}</div>
          <div v-if="allRatings.length > 1" class="crom-compact-info__lowest">
            lowest of {{ allRatings.length }}
          </div>
          <div v-if="props.securityLabel" class="crom-compact-info__unsecured">
            <strong :class="{ 'uns': securityStatus === 'UNSECURED' || props.securityLabel?.toLowerCase().includes('unsecured') }">{{ props.securityLabel }}</strong>
          </div>
          <div v-else-if="securityStatus === 'UNSECURED'" class="crom-compact-info__unsecured">
            <strong class="uns">Unsecured</strong>
          </div>
        </div>
      </div>

      <!-- Variant C: Mobile Meter -->
      <div v-else-if="variant === 'mobile'" class="crom-mobile-layout">
        <div class="crom-mobile-section-left">
          <div class="crom-mobile-label">
            Rating 
            <button 
              type="button" 
              class="crom-info-btn" 
              aria-label="About Credit Risk-o-Meter"
              @click.stop="showFullDialog = true"
            >
              i
            </button>
          </div>
          
          <div class="crom-compact-svg">
            <svg viewBox="0 0 140 82" width="86" role="img" :aria-label="ariaText">
              <!-- Semicircle Segments -->
              <path 
                v-for="i in nSegments" 
                :key="i - 1" 
                :d="getSegmentPath(70, 70, 38, 62, 180 - (i - 1) * segmentStep - 0.8, 180 - i * segmentStep + 0.8)" 
                :fill="getSegmentColor(i - 1)" 
              />
              
              <!-- Needle (Figma-exact tapered single polygon) -->
              <g :style="{ transform: `rotate(${needleRotation}deg)`, transformOrigin: '70px 70px', transition: 'transform 1.6s cubic-bezier(0.25, 0.8, 0.25, 1)' }">
                <polygon 
                  :points="getCompactNeedle" 
                  fill="#19204D" 
                />
                <circle 
                  :cx="70" 
                  :cy="70" 
                  r="6" 
                  fill="#19204D" 
                  stroke="#fff" 
                  stroke-width="2" 
                />
              </g>
            </svg>
          </div>
        </div>

        <div class="crom-mobile-rating-val">
          {{ isInc ? 'INC' : governingSymbol }}
        </div>

        <div class="crom-mobile-info">
          <div>{{ governingCra }}</div>
          <div v-if="props.securityLabel || securityStatus !== 'UNKNOWN'" class="crom-mobile-info__security">
            <strong v-if="props.securityLabel" :class="{ 'uns': securityStatus === 'UNSECURED' || props.securityLabel?.toLowerCase().includes('unsecured') }">{{ props.securityLabel }}</strong>
            <strong v-else-if="securityStatus === 'UNSECURED'" class="uns">Unsecured</strong>
            <strong v-else-if="securityStatus === 'SECURED'">Secured</strong>
          </div>
        </div>
      </div>

      <!-- Variant D: Figma Rating Card (Matches figma/image exactly) -->
      <div v-else-if="variant === 'card'" class="crom-card-layout">
        <div class="crom-card-info">
          <div class="crom-card-info__rating">
            <span class="rating-val">{{ isInc ? 'INC' : governingSymbol }}</span>
            <button 
              type="button" 
              class="crom-info-btn" 
              aria-label="About Credit Risk-o-Meter"
              @click.stop="showFullDialog = true"
            >
              i
            </button>
          </div>
          <div class="crom-card-info__sub">
            {{ governingCra }} &bull; {{ isInc ? 'Issuer not cooperating' : LABELS[level - 1] }}
          </div>
        </div>
        
        <div class="crom-card-svg">
          <svg viewBox="0 0 140 82" width="66" role="img" :aria-label="ariaText">
            <!-- Semicircle Segments -->
            <path 
              v-for="i in nSegments" 
              :key="i - 1" 
              :d="getSegmentPath(70, 70, 38, 62, 180 - (i - 1) * segmentStep - 0.8, 180 - i * segmentStep + 0.8)" 
              :fill="getSegmentColor(i - 1)" 
            />
            
            <!-- Needle (Figma-exact tapered single polygon) -->
            <g :style="{ transform: `rotate(${needleRotation}deg)`, transformOrigin: '70px 70px', transition: 'transform 1.6s cubic-bezier(0.25, 0.8, 0.25, 1)' }">
              <polygon 
                :points="getCompactNeedle" 
                fill="#19204D" 
              />
              <circle 
                :cx="70" 
                :cy="70" 
                r="6" 
                fill="#19204D" 
                stroke="#fff" 
                stroke-width="2" 
              />
            </g>
          </svg>
        </div>
      </div>
    </div>

    <!-- Info Dialog for Compact / Mobile Popup -->
    <v-dialog v-model="showFullDialog" max-width="840px" transition="dialog-bottom-transition">
      <v-card class="crom-dialog-card" style="position: relative; border-radius: 16px; padding: 24px; background: #fff;">
        <!-- Absolute close button matching figma/image exactly -->
        <button 
          type="button" 
          class="crom-dialog-close-btn" 
          aria-label="Close dialog"
          style="position: absolute; top: 16px; right: 16px; background: transparent; border: none; font-size: 18px; cursor: pointer; color: var(--crom-muted); z-index: 10;"
          @click="showFullDialog = false"
        >
          ✕
        </button>
        <v-card-text class="crom-dialog-body" style="padding: 0;">
          <!-- Render full-variant inside dialog recursively -->
          <CreditRiskMeter 
            :meter="props.meter" 
            variant="full" 
            :show-disclaimer="true"
            :loading="props.loading"
            :security-label="props.securityLabel"
          />
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, onMounted } from 'vue';
import CreditRiskMeterSkeleton from '@/components/skeletons/CreditRiskMeterSkeleton.vue';

// -------------------------------------------------------------
// Constants verbatim from design.html & SEBI circular
// -------------------------------------------------------------
const COLORS = ['#08A04B', '#7FFF00', '#FFFF33', '#C68E17', '#FF8C00', '#F70D1A'];
const INC_COLOR = '#9E9E9E';

const LABELS = [
    'Lowest Credit Risk',
    'Very Low Credit Risk',
    'Low Credit Risk',
    'Moderate Credit Risk',
    'Moderate risk of Default',
    'High to Very high Risk of Default'
];

const LABEL_LINES = [
    ['Lowest', 'Credit Risk'],
    ['Very Low', 'Credit Risk'],
    ['Low Credit', 'Risk'],
    ['Moderate', 'Credit Risk'],
    ['Moderate', 'risk of Default'],
    ['High to Very high', 'Risk of Default']
];

const LONG_SYMS = [
    ['AAA'],
    ['AA+', 'AA', 'AA-'],
    ['A+', 'A', 'A-'],
    ['BBB+', 'BBB', 'BBB-'],
    ['BB+', 'BB', 'BB-'],
    ['B+ to D']
];

const SHORT_SYMS = [
    ['A1+'],
    ['A1'],
    ['A2'],
    ['A3'],
    ['A4+'],
    ['A4 / D']
];

const DISC_STD = 'The Credit risk-o-meter is based on evaluation of the credit risk of the issuer and does not constitute investment advice or a recommendation to invest. Investments in debt securities are also subject to market and liquidity risks.';
const DISC_AT1 = 'The Credit Risk-o-Meter is based on evaluation of the credit risk of the issuer and does not reflect the unique structural risks associated with Unsecured Perpetual Bonds (such as AT1 Bonds). These instruments may carry the risk of total loss of invested capital. Investors are advised to read the Information Memorandum/Private placement Memorandum carefully before investing.';

// -------------------------------------------------------------
// Type Definitions
// -------------------------------------------------------------
export interface CraRating {
    id?: string;
    isin?: string;
    craCode?: string;
    cra_code?: string;
    ratingRaw?: string;
    rating_raw?: string;
    symbol?: string;
    scale?: string;
    isInc?: boolean;
    is_inc?: boolean;
    status?: string;
    ratingDate?: string;
    rating_date?: string;
}

export interface CreditRiskMeterData {
    isin?: string;
    level?: number;
    isInc?: boolean;
    is_inc?: boolean;
    scale?: string;
    governingRatingId?: string;
    governing_rating_id?: string;
    governingSymbol?: string;
    governing_symbol?: string;
    governingCra?: string;
    governing_cra?: string;
    allRatings?: CraRating[];
    all_ratings?: CraRating[];
    securityStatus?: string;
    security_status?: string;
    isPerpetual?: boolean;
    is_perpetual?: boolean;
    disclaimerVariant?: string;
    disclaimer_variant?: string;
    state?: string;
    computedAt?: string;
    computed_at?: string;
    publishedAt?: string;
    published_at?: string;
    version?: number;
}

// -------------------------------------------------------------
// Props & Emits
// -------------------------------------------------------------
const props = withDefaults(defineProps<{
    meter?: CreditRiskMeterData | null;
    variant?: 'full' | 'compact' | 'mobile' | 'card';
    showDisclaimer?: boolean;
    compactWidth?: number | string;
    loading?: boolean;
    securityLabel?: string | null;
}>(), {
    meter: null,
    variant: 'full',
    showDisclaimer: true,
    compactWidth: 132,
    loading: false,
    securityLabel: null
});

const showFullDialog = ref(false);

const isMounted = ref(false);

onMounted(() => {
    setTimeout(() => {
        isMounted.value = true;
    }, 100);
});

const needleRotation = computed(() => {
    if (!isMounted.value) {
        return 0;
    }
    return 180 - needleAngle.value;
});

// -------------------------------------------------------------
// Computed Fields with Snake vs Camel Case Resilience
// -------------------------------------------------------------
const isin = computed(() => props.meter?.isin || '');
const level = computed(() => {
    const rawVal = props.meter?.level;
    return typeof rawVal === 'number' ? rawVal : 1;
});
const isInc = computed(() => Boolean(props.meter?.isInc ?? props.meter?.is_inc ?? false));
const scale = computed(() => (props.meter?.scale || 'LONG').toUpperCase());

const governingSymbol = computed(() => props.meter?.governingSymbol || props.meter?.governing_symbol || '');
const governingCra = computed(() => props.meter?.governingCra || props.meter?.governing_cra || '');
const allRatings = computed(() => props.meter?.allRatings || props.meter?.all_ratings || []);

const securityStatus = computed(() => (props.meter?.securityStatus || props.meter?.security_status || 'UNKNOWN').toUpperCase());
const isPerpetual = computed(() => Boolean(props.meter?.isPerpetual ?? props.meter?.is_perpetual ?? false));
const disclaimerVariant = computed(() => (props.meter?.disclaimerVariant || props.meter?.disclaimer_variant || 'STANDARD').toUpperCase());
const state = computed(() => (props.meter?.state || 'OK').toUpperCase());

const formattedRatingDate = computed(() => {
    const rawDate = props.meter?.publishedAt || props.meter?.published_at || props.meter?.computedAt || props.meter?.computed_at;
    if (!rawDate) return '';
    try {
        const d = new Date(rawDate);
        if (Number.isNaN(d.getTime())) return '';
        const day = d.getDate();
        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const month = months[d.getMonth()];
        const year = d.getFullYear();
        return `${day} ${month} ${year}`;
    } catch {
        return '';
    }
});

function formatSingleRatingDate(rawDate?: string) {
    if (!rawDate) return '';
    try {
        const d = new Date(rawDate);
        if (Number.isNaN(d.getTime())) return '';
        const day = d.getDate();
        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const month = months[d.getMonth()];
        const year = d.getFullYear();
        return `${day} ${month} ${year}`;
    } catch {
        return '';
    }
}

const isRatingStale = computed(() => {
    const rawDate = props.meter?.publishedAt || props.meter?.published_at || props.meter?.computedAt || props.meter?.computed_at;
    if (!rawDate) return false;
    try {
        const ratingDate = new Date(rawDate);
        if (Number.isNaN(ratingDate.getTime())) return false;
        
        // System date: October 15, 2026
        const systemDate = new Date('2026-10-15T00:00:00Z');
        
        const diffMs = systemDate.getTime() - ratingDate.getTime();
        const diffDays = diffMs / (1000 * 60 * 60 * 24);
        
        return diffDays > 365;
    } catch (error) {
        console.error('Error calculating rating staleness:', error);
        return false;
    }
});

// Segment and mathematical parameters
const nSegments = computed(() => (isInc.value ? 7 : 6));
const segmentStep = computed(() => 180 / nSegments.value);

const needleAngle = computed(() => {
    if (isInc.value) {
        return 180 - 6.5 * segmentStep.value;
    }
    const clampedLevel = Math.max(1, Math.min(6, level.value));
    return 180 - (clampedLevel - 0.5) * 30;
});

// Aria-label for accessibility
const ariaText = computed(() => {
    if (state.value !== 'OK') {
        return 'Credit Risk-o-Meter not available';
    }
    const labelText = isInc.value ? 'Issuer not cooperating' : LABELS[level.value - 1] || '';
    const secText = securityStatus.value === 'UNSECURED' ? 'Unsecured.' : 'Secured.';
    return `Credit Risk-o-Meter: ${labelText}. Rating ${governingSymbol.value} by ${governingCra.value}. ${secText}`;
});

// -------------------------------------------------------------
// Mathematical SVG calculation helpers
// -------------------------------------------------------------
function polarToCartesian(cx: number, cy: number, r: number, angleInDegrees: number) {
    const angleInRadians = (angleInDegrees * Math.PI) / 180;
    return {
        x: cx + r * Math.cos(angleInRadians),
        y: cy - r * Math.sin(angleInRadians)
    };
}

function getSegmentPath(cx: number, cy: number, r1: number, r2: number, startAngle: number, endAngle: number) {
    const pStartOuter = polarToCartesian(cx, cy, r2, startAngle);
    const pEndOuter = polarToCartesian(cx, cy, r2, endAngle);
    const pEndInner = polarToCartesian(cx, cy, r1, endAngle);
    const pStartInner = polarToCartesian(cx, cy, r1, startAngle);
    return `M${pStartOuter.x},${pStartOuter.y} A${r2},${r2} 0 0 1 ${pEndOuter.x},${pEndOuter.y} L${pEndInner.x},${pEndInner.y} A${r1},${r1} 0 0 0 ${pStartInner.x},${pStartInner.y} Z`;
}

const getNeedleEndpoints = computed(() => {
    const angle = 180;
    const cx = 280;
    const cy = 262;
    const rLen = 84; // Tip length
    const rShoulder = 68; // Shoulder length
    const deltaAngle = 4.5; // Offset angle for shoulder in degrees

    const p1 = { x: cx, y: cy };
    const p2 = polarToCartesian(cx, cy, rShoulder, angle + deltaAngle);
    const p3 = polarToCartesian(cx, cy, rLen, angle);
    const p4 = polarToCartesian(cx, cy, rShoulder, angle - deltaAngle);

    return `${p1.x.toFixed(2)},${p1.y.toFixed(2)} ${p2.x.toFixed(2)},${p2.y.toFixed(2)} ${p3.x.toFixed(2)},${p3.y.toFixed(2)} ${p4.x.toFixed(2)},${p4.y.toFixed(2)}`;
});

const getCompactNeedle = computed(() => {
    const angle = 180;
    const cx = 70;
    const cy = 70;
    const rLen = 35; // Tip length
    const rShoulder = 28; // Shoulder length
    const deltaAngle = 4.5; // Offset angle for shoulder in degrees

    const p1 = { x: cx, y: cy };
    const p2 = polarToCartesian(cx, cy, rShoulder, angle + deltaAngle);
    const p3 = polarToCartesian(cx, cy, rLen, angle);
    const p4 = polarToCartesian(cx, cy, rShoulder, angle - deltaAngle);

    return `${p1.x.toFixed(2)},${p1.y.toFixed(2)} ${p2.x.toFixed(2)},${p2.y.toFixed(2)} ${p3.x.toFixed(2)},${p3.y.toFixed(2)} ${p4.x.toFixed(2)},${p4.y.toFixed(2)}`;
});

// Segment and color properties
function getSegmentColor(index: number) {
    if (isInc.value && index === 6) {
        return INC_COLOR;
    }
    return COLORS[index] || INC_COLOR;
}

function isSegmentActive(index: number) {
    if (isInc.value) {
        return index === 6;
    }
    return index === level.value - 1;
}

function getSegmentSymbols(index: number): string[] {
    if (isInc.value && index === 6) {
        return ['INC'];
    }
    const syms = LONG_SYMS;
    return syms[index] || [];
}

// Outer Labels (Lowest Risk, etc.)
function getSegmentOuterLabels(index: number): string[] {
    if (isInc.value && index === 6) {
        return ['Issuer not', 'cooperating'];
    }
    return LABEL_LINES[index] || [];
}

// Outer label color
function getSymbolColor(index: number) {
    if (isInc.value && index === 6) {
        return '#fff';
    }
    return '#000000';
}

function getSymbolRadius(index: number, k: number) {
    const rows = getSegmentSymbols(index);
    const r1 = 92;
    const r2 = 165;
    return (r1 + r2) / 2 + ((rows.length - 1) / 2 - k) * 15;
}

function getOuterLabelPosition(index: number) {
    const m = getSegmentMiddleAngle(index);
    // Push extreme left/right labels further outward to prevent overlapping the dial arcs
    let rOffset = 46;
    if (index === 0 || index === 5) {
        rOffset = 72; // Pushes index 0 further left and index 5 further right
    } else if (index === 1 || index === 4) {
        rOffset = 54; // Slightly pushes index 1 and 4 outward for spacing
    }
    return polarToCartesian(280, 262, 165 + rOffset, m);
}

function getSegmentMiddleAngle(index: number) {
    return 180 - index * segmentStep.value - segmentStep.value / 2;
}

// -------------------------------------------------------------
// Format Text Row & Disclaimer
// -------------------------------------------------------------
const formatAllRatings = computed(() => {
    return allRatings.value
        .map(r => {
            const code = r.craCode || r.cra_code || '';
            const sym = r.symbol || r.ratingRaw || r.rating_raw || '';
            return `${code} ${sym}`;
        })
        .filter(str => str.trim())
        .join(' · ');
});

const disclaimerText = computed(() => {
    // AT1 variant if both unsecured and perpetual, or explicitly defined as AT1 variant
    if (disclaimerVariant.value === 'UNSECURED_PERPETUAL' || (securityStatus.value === 'UNSECURED' && isPerpetual.value)) {
        return DISC_AT1;
    }
    return DISC_STD;
});
</script>

<style scoped>
.crom {
  font-family: Inter, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  --crom-ink: #19204D;
  --crom-muted: #5b6784;
  --crom-line: #dfe5f2;
  --crom-bg: #f4f7fc;
  --crom-brand: #0a2fa5;
  --crom-brand-2: #e7efff;
  --crom-warn: #b26a00;
  --crom-ok: #0a7a3c;
  --crom-bad: #c62828;
  --crom-l1: #08A04B;
  --crom-l2: #7FFF00;
  --crom-l3: #FFFF33;
  --crom-l4: #C68E17;
  --crom-l5: #FF8C00;
  --crom-l6: #F70D1A;
  --crom-inc: #9E9E9E;
  
  color: var(--crom-ink);
  width: 100%;
}

svg text {
  font-family: inherit;
}

/* ----------------- Banners ----------------- */
.crom-banner {
  display: flex;
  gap: 12px;
  align-items: flex-start;
  padding: 12px 16px;
  border-radius: 10px;
  font-size: 13.5px;
  line-height: 1.4;
}

.crom-banner--unrated {
  background-color: #fff5f5;
  border: 1px solid #f3b4b4;
  color: #721c24;
}

.crom-banner--sovereign {
  background-color: #f1f4fb;
  border: 1px solid #d5dcee;
  color: #1b2e4f;
}

.crom-banner--stale {
  background-color: #fffbeb;
  border: 1px solid #fde047;
  color: #854d0e;
  margin-top: 16px;
  margin-bottom: 8px;
  width: 100%;
}

.crom-banner__icon {
  font-size: 18px;
  flex-shrink: 0;
}

.crom-banner__text {
  flex-grow: 1;
}

/* ----------------- Full Variant (Figma Split-Column Details Page layout) ----------------- */
.crom-full-layout {
  display: flex;
  flex-direction: column;
  width: 100%;
}

/* Figma-aligned Full Header styling */
.crom-full-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  width: 100%;
  margin-bottom: 16px;
  padding-bottom: 8px;
  border-bottom: 1px solid var(--crom-line);
}

.crom-full-header__title {
  margin: 0;
  font-size: 20px;
  font-weight: 700;
  color: var(--crom-ink);
  line-height: 1.1;
}

.crom-full-header__subtitle {
  margin: 0;
  font-size: 13px;
  font-weight: 500;
  color: var(--crom-ink);
  line-height: 1.3;
}

.crom-full-body {
  display: flex;
  gap: 24px;
  width: 100%;
  align-items: center;
}

.crom-full-details-col {
  flex: 1 1 45%;
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-width: 0;
}

.crom-full-gauge-col {
  flex: 1 1 55%;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 0;
}

/* Rating Summary Box styling (Matches Figma/SMEST exactly) */
.crom-rating-summary-box {
  display: flex;
  align-items: center;
  gap: 12px;
  background-color: #f2eef9;
  border-radius: 12px;
  padding: 10px 14px;
  width: 100%;
}

.crom-rating-summary-box__rating {
  font-size: 28px;
  font-weight: 700;
  color: #19204d;
  line-height: 1.1;
  text-align: center;
  min-width: 80px;
  border-right: 1.5px solid var(--crom-line);
  padding-right: 12px;
  white-space: nowrap;
}

.crom-rating-summary-box__text {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  text-align: left;
}

.crom-rating-summary-box__text .risk-label {
  font-size: 10px;
  font-weight: 700;
  color: var(--crom-muted);
  letter-spacing: 0.05em;
  text-transform: uppercase;
}

.crom-rating-summary-box__text .rating-agency-val {
  font-size: 14px;
  font-weight: 600;
  color: #19204d;
}

/* Clean Row list styling */
.crom-details-rows-col {
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
}

.crom-details-row-clean {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px 0;
  font-size: 12px;
  border-bottom: 1px dashed var(--crom-line);
}

.crom-details-row-clean:last-child {
  border-bottom: none;
}

.crom-details-row-clean .label {
  color: var(--crom-muted);
}

.crom-details-row-clean .value {
  color: var(--crom-ink);
  font-weight: 600;
}

.crom-details-row-clean .uns {
  color: var(--crom-bad);
  font-weight: 800;
}

.crom-svg-container {
  width: 100%;
  max-width: 620px;
  margin: 0 auto;
}

.crom-details-security-val {
  font-weight: 700;
  color: var(--crom-ink);
  font-size: 12.5px;
}

.crom-details-date-label {
  color: var(--crom-muted);
  font-size: 11px;
}

.crom-all-ratings-row {
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;
}

.crom-all-ratings-title {
  color: var(--crom-muted);
  font-size: 11px;
}

.crom-all-ratings-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
}

.crom-all-ratings-item {
  font-size: 11.5px;
  color: var(--crom-ink);
  font-weight: 600;
  display: flex;
  align-items: center;
  width: 100%;
}

.crom-all-ratings-item__cra {
  color: var(--crom-muted);
  font-weight: 500;
  flex: 1;
  text-align: left;
}

.crom-all-ratings-item__date {
  font-size: 10px;
  color: var(--crom-muted);
  margin-left: 6px;
}

.crom-all-ratings-item__sym {
  width: 42px;
  text-align: left;
  display: inline-block;
}

/* Mobile responsive adjustments */
@media (max-width: 768px) {
  .crom-full-body {
    flex-direction: column;
    gap: 16px;
  }
  
  .crom-full-details-col {
    width: 100%;
    order: 2; /* Put details below on mobile, gauge on top */
  }
  
  .crom-full-gauge-col {
    width: 100%;
    order: 1;
  }

  .crom-rating-summary-box {
    padding: 8px 10px;
    gap: 8px;
    border-radius: 8px;
  }
  
  .crom-rating-summary-box__rating {
    font-size: 20px;
    min-width: 50px;
    padding-right: 8px;
  }
  
  .crom-rating-summary-box__text .risk-label {
    font-size: 9px;
  }
  
  .crom-rating-summary-box__text .rating-agency-val {
    font-size: 11px;
  }

  .crom-details-row-clean {
    font-size: 10.5px;
    padding: 4px 0;
  }

  .crom-details-security-val {
    font-size: 10.5px;
  }

  .crom-details-date-label {
    font-size: 10px;
  }

  .crom-all-ratings-title {
    font-size: 10px;
  }

  .crom-all-ratings-item {
    font-size: 10px;
  }

  .crom-all-ratings-item__date {
    font-size: 9px;
  }

  .crom-dialog-body {
    padding: 16px 16px !important;
  }

  .crom-dialog-card {
    padding: 12px !important;
  }
}

.crom-disclaimer {
  font-size: 12px;
  color: var(--crom-muted);
  margin-top: 12px;
  border-top: 1px dashed var(--crom-line);
  padding-top: 12px;
  line-height: 1.5;
  text-align: justify;
}

/* ----------------- Compact Variant ----------------- */
.crom-compact-layout {
  display: flex;
  gap: 12px;
  align-items: flex-end;
  width: auto;
}

.crom-compact-svg {
  display: flex;
  align-items: flex-end;
  flex-shrink: 0;
}

.crom-compact-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  font-size: 12.5px;
  line-height: 1.3;
}

.crom-compact-info__rating {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 16px;
  font-weight: 700;
}

.crom-compact-info__cra {
  color: var(--crom-muted);
}

.crom-compact-info__lowest {
  color: var(--crom-muted);
  font-size: 11.5px;
}

.crom-compact-info__unsecured .uns {
  color: var(--crom-bad);
  font-weight: 800;
}

/* Info Circle Icon Button */
.crom-info-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background-color: #005dab;
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  border: none;
  cursor: pointer;
  padding: 0;
  transition: background-color 0.2s;
  vertical-align: middle;
}

.crom-info-btn:hover {
  background-color: var(--crom-brand);
}

/* ----------------- Mobile Variant ----------------- */
.crom-mobile-layout {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
  padding: 8px 0;
}

.crom-mobile-section-left {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.crom-mobile-label {
  font-size: 12px;
  color: var(--crom-muted);
  display: flex;
  align-items: center;
  gap: 6px;
}

.crom-mobile-rating-val {
  font-size: 24px;
  font-weight: 800;
  color: var(--crom-ink);
}

.crom-mobile-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
  font-size: 12.5px;
  text-align: right;
}

.crom-mobile-info__security .uns {
  color: var(--crom-bad);
  font-weight: 800;
}

/* ----------------- Dialog Styling ----------------- */
.crom-dialog-card {
  border-radius: 12px !important;
  box-shadow: 0 4px 30px rgba(15, 27, 61, 0.2) !important;
  background-color: #fff !important;
}

.crom-dialog-title {
  display: flex;
  align-items: center;
  font-weight: 700 !important;
  font-size: 18px !important;
  color: var(--crom-ink) !important;
  padding: 16px 24px !important;
  border-bottom: 1px solid var(--crom-line);
}

.crom-dialog-close-btn {
  background: transparent;
  border: none;
  font-size: 18px;
  cursor: pointer;
  color: var(--crom-muted);
  padding: 4px 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: color 0.2s, background-color 0.2s;
}

.crom-dialog-close-btn:hover {
  color: var(--crom-ink);
  background-color: #f1f4f9;
}

.crom-dialog-body {
  padding: 32px 40px !important;
  overflow-y: auto;
  max-height: calc(95vh - 70px);
}

/* ----------------- Card Variant (Matches figma/image exactly) ----------------- */
.crom-card-layout {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  background: var(--crom-bg);
  border: 1px solid var(--crom-line);
  border-radius: 8px;
  padding: 8px 12px;
  box-shadow: none;
}

.crom-card-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
  text-align: left;
}

.crom-card-info__rating {
  display: flex;
  align-items: center;
  gap: 6px;
}

.crom-card-info__rating .rating-val {
  font-size: 17px;
  font-weight: 700;
  color: var(--crom-ink);
  line-height: 1;
}

.crom-card-info__sub {
  font-size: 12px;
  font-weight: 500;
  color: var(--crom-muted);
  line-height: 1.2;
}

.crom-card-svg {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-shrink: 0;
}
</style>